import { Transaction, payWithCredits } from '@principle-theorem/principle-core';
import {
  TransactionProvider,
  TransactionStatus,
  TransactionType,
  type IAccountCredit,
  type IAccountCreditAttributed,
  type IAccountCreditExtendedData,
  type IPractice,
  type IRefundCreditResults,
  type IStaffer,
  type ITransaction,
  type IUsedAccountCredit,
} from '@principle-theorem/principle-core/interfaces';
import {
  isSameRef,
  toTimestamp,
  type AtLeast,
  type DocumentReference,
  type WithRef,
} from '@principle-theorem/shared';
import { omit } from 'lodash';

export interface IAccountCreditTransactionBuildResult {
  transaction: ITransaction<IAccountCreditExtendedData>;
  remaining: number;
  alteredCredits: IAccountCreditAttributed[];
}

export interface IAccountCreditTransactionBuildParams {
  baseTransaction: AtLeast<ITransaction, 'to' | 'from' | 'amount'>;
  /**
   * Ordered by the caller's preferred consumption order, typically credits
   * reserved for the matching practitioner or treatment category first,
   * followed by unreserved credits.
   */
  credits: WithRef<IAccountCredit>[];
  amount: number;
  practiceRef: DocumentReference<IPractice>;
  attributedTo?: DocumentReference<IStaffer>;
}

export interface IRefundCreditTransactionBuildParams {
  transaction: WithRef<ITransaction<IAccountCreditExtendedData>>;
  amount: number;
  accountCreditsUsed: IUsedAccountCredit[];
  accountCreditsToRefund: IRefundCreditResults;
}

/**
 * Builds the financial records for spending and refunding account credit so all
 * flows preserve the same provider, status, and per-credit refund invariants.
 */
export class AccountCreditTransactionBuilder {
  /**
   * Builds an incoming account-credit transaction without persistence so manual
   * and automatic capture paths cannot drift in transaction shape.
   */
  static buildAccountCreditTransaction(
    params: IAccountCreditTransactionBuildParams
  ): IAccountCreditTransactionBuildResult {
    const { remaining, alteredCredits } = payWithCredits(
      params.credits,
      params.amount
    );

    const transaction = Transaction.init<IAccountCreditExtendedData>({
      ...params.baseTransaction,
      ...Transaction.internalReference(TransactionProvider.AccountCredit),
      amount: params.amount,
      type: TransactionType.Incoming,
      status: TransactionStatus.Complete,
      practiceRef: params.practiceRef,
      extendedData: {
        accountCreditsUsed: alteredCredits.map((alteredCredit) => ({
          name: alteredCredit.accountCredit.description,
          ref: alteredCredit.accountCredit.ref,
          amount: alteredCredit.amount,
          refunded: 0,
          totalRefunded: 0,
        })),
      },
      attributedTo: params.attributedTo,
    });

    return { transaction, remaining, alteredCredits };
  }

  /**
   * Builds an outgoing refund transaction while carrying refunds onto only the
   * affected credits, preserving the outstanding credit history for all others.
   */
  static buildRefundCreditTransaction(
    params: IRefundCreditTransactionBuildParams
  ): ITransaction<IAccountCreditExtendedData> {
    return Transaction.init<IAccountCreditExtendedData>({
      ...omit(params.transaction, 'ref'),
      amount: params.amount,
      type: TransactionType.Outgoing,
      status: TransactionStatus.Complete,
      createdAt: toTimestamp(),
      updatedAt: toTimestamp(),
      extendedData: {
        accountCreditsUsed: params.accountCreditsUsed.map((accountCredit) => {
          const refundedCredit =
            params.accountCreditsToRefund.alteredCredits.find((alteredCredit) =>
              isSameRef(alteredCredit.accountCreditRef, accountCredit)
            );
          if (!refundedCredit) {
            return accountCredit;
          }
          return {
            ...accountCredit,
            totalRefunded: accountCredit.totalRefunded + refundedCredit.amount,
            refunded: refundedCredit.amount,
          };
        }),
      },
    });
  }
}
